// src/redux/modules/creditsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getAxiosInstance } from "../../utils/apiClient";
import { getTokens } from "../../utils/tokens";

interface CreditsBalance {
  credits: number;
  totalCredits: number;
  usedCredits: number;
  subscriptionTier?: string;
  lastUpdated: string;
}

interface CreditsResponse {
  success: boolean;
  data: CreditsBalance;
  message?: string;
}

interface CreditsState {
  balance: CreditsBalance | null;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: CreditsState = {
  balance: null,
  loading: false,
  error: null,
  lastFetched: null,
};

// Async thunk for fetching credits balance
export const fetchCreditsBalance = createAsyncThunk(
  "credits/fetchBalance",
  async (_, { rejectWithValue }) => {
    const tokens = getTokens();

    if (!tokens?.access_token) {
      return rejectWithValue("Missing auth tokens");
    }

    try {
      const response = await getAxiosInstance().get<CreditsResponse>(
        "/api/monetization/credits",
        {
          headers: {
            "Content-Type": "application/json",
            [process.env
              .REACT_APP_AUTH_TOKEN as any]: `Bearer ${tokens.access_token}`,
            [process.env
              .REACT_APP_FIREBASE_AUTH_TOKEN as any]: `Bearer ${tokens.firebase_token}`,
            "ngrok-skip-browser-warning": "true",
          },
        }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        return rejectWithValue(
          response.data.message || "Failed to fetch credits balance"
        );
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || "Failed to fetch credits balance";
      return rejectWithValue(errorMessage);
    }
  }
);

const creditsSlice = createSlice({
  name: "credits",
  initialState,
  reducers: {
    clearCredits: state => {
      state.balance = null;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCreditsBalance.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCreditsBalance.fulfilled,
        (state, action: PayloadAction<CreditsBalance>) => {
          state.loading = false;
          state.balance = action.payload;
          state.error = null;
          state.lastFetched = Date.now();
        }
      )
      .addCase(
        fetchCreditsBalance.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload || "Failed to fetch credits balance";
        }
      );
  },
});

export const { clearCredits } = creditsSlice.actions;
export default creditsSlice.reducer;
